"use client";

import type {
  AssistantStaticBubbleVariantProps,
  UserStaticBubbleVariantProps,
} from "@ui/components/core/BaseChat/styles";
import type {
  ChatPlayerCompiledChat,
  ChatPlayerMessage,
  ChatPlayerPart,
  ChatPlayerPartSnapshot,
  ChatPlayerRole,
  ChatPlayerScenarioSource,
  ChatPlayerTranscriptSource,
} from "./chat-player-types";
import type { ChatPlayerPlayback } from "./chat-player-playback-driver";

import { MessagesList } from "@ui/components/core/BaseChat/BaseChat";
import { assistantStaticBubbleVariants, userStaticBubbleVariants } from "@ui/components/core/BaseChat/styles";
import { cn } from "@ui/lib/utils";
import { clsx } from "clsx";
import { AnimatePresence, LayoutGroup, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { memo, useEffect, useMemo } from "react";

import { ChatPlayerPlaybackDriver, getInitialChatPlayerPlaybackTimeMs } from "./chat-player-playback-driver";
import {
  ChatPlayerPlaybackProvider,
  useChatPlayerPlaybackKey,
  useChatPlayerSpeechVisibleWordIndex,
  useChatPlayerToolStatus,
  useChatPlayerVisibleMessages,
  useChatPlayerVisibleParts,
} from "./chat-player-playback-store";
import { getChatPlayerSpeechPartSnapshot, getChatPlayerToolPartSnapshot } from "./chat-player-playback";
import { ChatPlayerPartContent, type ChatPlayerPartRegistry } from "./chat-player-parts";
import { compileChatPlayerScenario } from "./chat-player-scenario";
import { compileChatPlayerTranscript } from "./chat-player-transcript";

export type { ChatPlayerPlayback } from "./chat-player-playback-driver";
export type { ChatPlayerPartRegistry } from "./chat-player-parts";

export type ChatPlayerSource = ChatPlayerScenarioSource | ChatPlayerTranscriptSource;

export type ChatPlayerProps = {
  source: ChatPlayerSource;
  anchor?: "top" | "bottom";
  animateInitialSnapshot?: boolean;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  className?: string;
  contentClassName?: string;
  onDurationChange?: (durationMs: number) => void;
  partRegistry?: ChatPlayerPartRegistry;
  playback?: ChatPlayerPlayback;
  userMessageVariants?: UserStaticBubbleVariantProps;
};

type CompiledChatPlayerViewProps = {
  chat: ChatPlayerCompiledChat;
  playback: ChatPlayerPlayback;
  anchor: "top" | "bottom";
  animateInitialSnapshot: boolean;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  className?: string;
  contentClassName?: string;
  partRegistry?: ChatPlayerPartRegistry;
  userMessageVariants: UserStaticBubbleVariantProps;
};

type ChatPlayerMessageViewProps = {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatPlayerMessage;
  partRegistry?: ChatPlayerPartRegistry;
  reducedMotion: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
};

type ChatPlayerPartBubbleProps = {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  messageId: string;
  part: ChatPlayerPart;
  partRegistry?: ChatPlayerPartRegistry;
  reducedMotion: boolean;
  role: ChatPlayerRole;
  userMessageVariants: UserStaticBubbleVariantProps;
};

const DEFAULT_PLAYBACK = { type: "auto", active: true } satisfies ChatPlayerPlayback;
const BUBBLE_ENTER_SCALE = 0.3;
const BUBBLE_ENTER_ROTATE_DEGREES = 1;
const BUBBLE_ENTER_Y = 0;
const BUBBLE_ENTER_TRANSITION = { duration: 0.7, ease: [0.19, 1, 0.22, 1] } as const;
const MESSAGE_PARTS_CLASS_NAME = "tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-flex-col tw-gap-2";
const TOOL_BUBBLE_VARIANTS = { rounded: "pill", tail: "none" } satisfies AssistantStaticBubbleVariantProps;
const MESSAGE_BUBBLE_RAIL_CLASS_NAMES = {
  assistant: "tw-mr-auto tw-flex tw-w-full tw-min-w-0 tw-max-w-[90%] tw-justify-start",
  user: "tw-ml-auto tw-flex tw-w-full tw-min-w-0 tw-max-w-[90%] tw-justify-end",
} satisfies Record<ChatPlayerRole, string>;
const MESSAGE_PARTS_ALIGNMENT_CLASS_NAMES = {
  assistant: "tw-items-start",
  user: "tw-items-end",
} satisfies Record<ChatPlayerRole, string>;
const PART_BUBBLE_ORIGIN_CLASS_NAMES = {
  assistant: "tw-origin-bottom-left",
  tool: "tw-origin-left",
  user: "tw-origin-bottom-right",
} satisfies Record<ChatPlayerRole | "tool", string>;
const defaultAssistantMessageVariants = { variant: "cream" } satisfies AssistantStaticBubbleVariantProps;
const defaultUserMessageVariants = { variant: "elevated" } satisfies UserStaticBubbleVariantProps;

export function ChatPlayer({
  anchor = "bottom",
  animateInitialSnapshot = false,
  assistantMessageVariants = defaultAssistantMessageVariants,
  className,
  contentClassName,
  onDurationChange,
  partRegistry,
  playback = DEFAULT_PLAYBACK,
  source,
  userMessageVariants = defaultUserMessageVariants,
}: ChatPlayerProps) {
  const chat = useMemo(() => compileChatPlayerSource(source), [source]);

  useEffect(() => {
    onDurationChange?.(chat.durationMs);
  }, [chat.durationMs, onDurationChange]);

  return (
    <CompiledChatPlayerView
      key={chat.scopeKey}
      anchor={anchor}
      animateInitialSnapshot={animateInitialSnapshot}
      assistantMessageVariants={assistantMessageVariants}
      chat={chat}
      className={className}
      contentClassName={contentClassName}
      partRegistry={partRegistry}
      playback={playback}
      userMessageVariants={userMessageVariants}
    />
  );
}

function compileChatPlayerSource(source: ChatPlayerSource) {
  return "segments" in source ? compileChatPlayerTranscript(source) : compileChatPlayerScenario(source);
}

function CompiledChatPlayerView({
  anchor,
  animateInitialSnapshot,
  assistantMessageVariants,
  chat,
  className,
  contentClassName,
  partRegistry,
  playback,
  userMessageVariants,
}: CompiledChatPlayerViewProps) {
  const initialTimeMs = getInitialChatPlayerPlaybackTimeMs(playback, chat.durationMs);

  return (
    <ChatPlayerPlaybackProvider chat={chat} initialTimeMs={initialTimeMs}>
      <ChatPlayerPlaybackDriver chat={chat} playback={playback} />
      <ChatPlayerMessagesView
        anchor={anchor}
        animateInitialSnapshot={animateInitialSnapshot}
        assistantMessageVariants={assistantMessageVariants}
        chat={chat}
        className={className}
        contentClassName={contentClassName}
        partRegistry={partRegistry}
        userMessageVariants={userMessageVariants}
      />
    </ChatPlayerPlaybackProvider>
  );
}

function ChatPlayerMessagesView({
  anchor,
  animateInitialSnapshot,
  assistantMessageVariants,
  chat,
  className,
  contentClassName,
  partRegistry,
  userMessageVariants,
}: Omit<CompiledChatPlayerViewProps, "playback">) {
  const prefersReducedMotion = useReducedMotion();
  const reducedMotion = Boolean(prefersReducedMotion);
  const playbackKey = useChatPlayerPlaybackKey();
  const visibleMessages = useChatPlayerVisibleMessages(chat);
  const playbackScopeKey = `${chat.scopeKey}-${playbackKey}`;

  return (
    <MessagesList
      anchor={anchor}
      className={cn("tw-overflow-hidden", className)}
      contentClassName={cn("tw-overflow-x-hidden tw-overflow-y-hidden tw-scrollbar-none tw-p-1.5", contentClassName)}
    >
      <LayoutGroup id={playbackScopeKey}>
        <AnimatePresence initial={!reducedMotion && animateInitialSnapshot} mode='sync'>
          {visibleMessages.map(message => (
            <ChatPlayerMessageView
              key={`${playbackScopeKey}-${message.id}`}
              assistantMessageVariants={assistantMessageVariants}
              message={message}
              partRegistry={partRegistry}
              reducedMotion={reducedMotion}
              userMessageVariants={userMessageVariants}
            />
          ))}
        </AnimatePresence>
      </LayoutGroup>
    </MessagesList>
  );
}

const ChatPlayerMessageView = memo(function ChatPlayerMessageView({
  assistantMessageVariants,
  message,
  partRegistry,
  reducedMotion,
  userMessageVariants,
}: ChatPlayerMessageViewProps) {
  const parts = useChatPlayerVisibleParts(message);

  return (
    <m.article className='tw-flex tw-w-full' {...getMessageMotionProps(reducedMotion)}>
      <div className={MESSAGE_BUBBLE_RAIL_CLASS_NAMES[message.role]}>
        <div className={clsx(MESSAGE_PARTS_CLASS_NAME, MESSAGE_PARTS_ALIGNMENT_CLASS_NAMES[message.role])}>
          {parts.map(part => (
            <ChatPlayerPartBubble
              key={part.id}
              assistantMessageVariants={assistantMessageVariants}
              messageId={message.id}
              part={part}
              partRegistry={partRegistry}
              reducedMotion={reducedMotion}
              role={message.role}
              userMessageVariants={userMessageVariants}
            />
          ))}
        </div>
      </div>
    </m.article>
  );
});

function getMessageMotionProps(reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      layout: false as const,
      initial: false as const,
      transition: { duration: 0 },
    };
  }

  return {
    layout: "position" as const,
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -6 },
    transition: {
      duration: 0.28,
      ease: [0.22, 1, 0.36, 1] as const,
      layout: { duration: 0.34, ease: [0.22, 1, 0.36, 1] as const },
    },
  };
}

function ChatPlayerPartBubble({
  assistantMessageVariants,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: ChatPlayerPartBubbleProps) {
  switch (part.type) {
    case "speech":
      return (
        <ChatPlayerSpeechPartBubble
          assistantMessageVariants={assistantMessageVariants}
          messageId={messageId}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
    case "tool":
      return (
        <ChatPlayerToolPartBubble
          assistantMessageVariants={assistantMessageVariants}
          messageId={messageId}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
    case "text":
    case "custom":
      return (
        <ChatPlayerPartBubbleFrame
          assistantMessageVariants={assistantMessageVariants}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
  }
}

function ChatPlayerSpeechPartBubble({
  assistantMessageVariants,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "messageId" | "part"> & {
  messageId: string;
  part: Extract<ChatPlayerPart, { type: "speech" }>;
}) {
  const visibleWordIndex = useChatPlayerSpeechVisibleWordIndex(messageId, part);
  const visiblePart = useMemo(() => getChatPlayerSpeechPartSnapshot(part, visibleWordIndex), [part, visibleWordIndex]);

  return (
    <ChatPlayerPartBubbleFrame
      assistantMessageVariants={assistantMessageVariants}
      part={visiblePart}
      partRegistry={partRegistry}
      reducedMotion={reducedMotion}
      role={role}
      userMessageVariants={userMessageVariants}
    />
  );
}

function ChatPlayerToolPartBubble({
  assistantMessageVariants,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "part"> & {
  part: Extract<ChatPlayerPart, { type: "tool" }>;
}) {
  const status = useChatPlayerToolStatus(messageId, part);
  const visiblePart = useMemo(() => getChatPlayerToolPartSnapshot(part, status), [part, status]);

  return (
    <ChatPlayerPartBubbleFrame
      assistantMessageVariants={assistantMessageVariants}
      part={visiblePart}
      partRegistry={partRegistry}
      reducedMotion={reducedMotion}
      role={role}
      userMessageVariants={userMessageVariants}
    />
  );
}

function ChatPlayerPartBubbleFrame({
  assistantMessageVariants,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "messageId" | "part"> & {
  part: ChatPlayerPartSnapshot;
}) {
  const bubbleOriginClassName =
    part.type === "tool" ? PART_BUBBLE_ORIGIN_CLASS_NAMES.tool : PART_BUBBLE_ORIGIN_CLASS_NAMES[role];

  return (
    <m.div
      className={clsx(
        "tw-min-w-0",
        getPartBubbleClassName(part, role, assistantMessageVariants, userMessageVariants),
        bubbleOriginClassName,
      )}
      {...getPartBubbleMotionProps(part, role, reducedMotion)}
    >
      <ChatPlayerPartContent part={part} partRegistry={partRegistry} reducedMotion={reducedMotion} role={role} />
    </m.div>
  );
}

function getPartBubbleMotionProps(part: ChatPlayerPartSnapshot, role: ChatPlayerRole, reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      initial: false as const,
      transition: { duration: 0 },
    };
  }

  const initialRotate =
    part.type === "tool" ? 0 : role === "user" ? -BUBBLE_ENTER_ROTATE_DEGREES : BUBBLE_ENTER_ROTATE_DEGREES;

  return {
    initial: {
      opacity: 0,
      rotate: initialRotate,
      scale: BUBBLE_ENTER_SCALE,
      y: BUBBLE_ENTER_Y,
    },
    animate: { opacity: 1, rotate: 0, scale: 1, y: 0 },
    transition: BUBBLE_ENTER_TRANSITION,
  };
}

function getPartBubbleClassName(
  part: ChatPlayerPartSnapshot,
  role: ChatPlayerRole,
  assistantMessageVariants: AssistantStaticBubbleVariantProps,
  userMessageVariants: UserStaticBubbleVariantProps,
) {
  if (part.type === "tool") {
    return assistantStaticBubbleVariants({ ...assistantMessageVariants, ...TOOL_BUBBLE_VARIANTS });
  }

  if (role === "user") {
    return userStaticBubbleVariants(userMessageVariants);
  }

  return assistantStaticBubbleVariants(assistantMessageVariants);
}
